import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import { WidgetProps } from "@rjsf/core";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import WidgetLabel from "./WidgetLabel";
import { Icon } from "components/Icon";

interface IconInputOptions {
    icon: IconName;
    inputType?: string;
    autoComplete?: string;
}

export const IconInputWidget: React.FC<WidgetProps> = (props) => {
    const {
        id,
        placeholder,
        required,
        readonly,
        disabled,
        value,
        label,
        onChange,
        onBlur,
        onFocus,
        autofocus,
        options,
        schema,
        uiSchema,
        rawErrors = [],
    } = props;

    const { icon, inputType = "text", autoComplete } = options as unknown as IconInputOptions;
    const isPassword = inputType === "password";
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const effectiveType = isPassword && isPasswordVisible ? "text" : inputType;

    const _onChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => onChange(value === "" ? options.emptyValue : value);
    const _onBlur = ({ target: { value } }: React.FocusEvent<HTMLInputElement>) => onBlur(id, value);
    const _onFocus = ({ target: { value } }: React.FocusEvent<HTMLInputElement>) => onFocus(id, value);
    const isInvalid = rawErrors.length > 0;
    const helpText = uiSchema?.["ui:help"];
    const hasErrorList = rawErrors.some((error) => error !== helpText);
    const describedBy = [hasErrorList && `${id}-error`, helpText && `help-${id}`].filter(Boolean).join(" ") || undefined;

    return (
        <>
            <WidgetLabel id={id} label={label} uiSchema={uiSchema} schema={schema} required={required} rawErrors={rawErrors} />
            <div className="position-relative">
                <Icon
                    icon={["fal", icon]}
                    className="position-absolute top-50 translate-middle-y text-gray-600 pe-none"
                    style={{ left: 13 }}
                />
                <Form.Control
                    id={id}
                    type={effectiveType}
                    placeholder={placeholder}
                    autoFocus={autofocus}
                    autoComplete={autoComplete}
                    required={required}
                    disabled={disabled}
                    readOnly={readonly}
                    isInvalid={isInvalid}
                    aria-invalid={isInvalid}
                    aria-describedby={describedBy}
                    className={isPassword ? "bg-image-none" : undefined}
                    value={value || value === 0 ? value : ""}
                    onChange={_onChange}
                    onBlur={_onBlur}
                    onFocus={_onFocus}
                    style={{ paddingLeft: 36, paddingRight: isPassword ? 52 : undefined }}
                />
                {isPassword && (
                    <button
                        type="button"
                        className={`position-absolute top-0 end-0 h-100 d-flex align-items-center justify-content-center bg-transparent border-0 border-start ${
                            isInvalid ? "border-color-danger text-form-invalid" : "text-gray-600"
                        }`}
                        onClick={() => setIsPasswordVisible((prev) => !prev)}
                        aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                        aria-pressed={isPasswordVisible}
                        disabled={disabled || readonly}
                        style={{ width: 44 }}
                    >
                        <Icon icon={["fal", isPasswordVisible ? "eye-slash" : "eye"]} />
                    </button>
                )}
            </div>
        </>
    );
};

export default IconInputWidget;
